import React from 'react'
import { toast } from 'react-toastify'
import { ContentCopy } from '@mui/icons-material'
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useMutation } from '@tanstack/react-query'
import PaymentAPI from 'api/faber/PaymentAPI'
import { AxiosError } from 'axios'
import PageLayout from 'common/Layout/Page/PageLayout'
import NormalText from 'common/Typography/NormalText'
import { ColorBoxContent } from 'common/ui/colorBox'
import { Formik } from 'formik'
import OrbitSession from 'hooks/useCookies'
import * as Yup from 'yup'

interface StripePayment {
  stripeInvoiceId: string
  amount: number
  status: string
  stripePaymentId: string
  catalogId: string
  currency: string
  name: string
  paymentDate: string
  promoCode: string
  userId: string
  userEmail: string
  stripeCustomerId: string
}

const validationSchema = Yup.object({
  last4: Yup.string()
    .matches(/^[0-9]{4}$/, 'Must be exactly 4 digits')
    .required('Last 4 digits are required'),
  amount: Yup.number()
    .typeError('Amount must be a number')
    .positive('Amount must be positive'),
  startDate: Yup.date().nullable().required('Start date is required'),
  endDate: Yup.date()
    .nullable()
    .required('End date is required')
    .when('startDate', (startDate, schema) => {
      return startDate
        ? schema.min(startDate, 'End date must be after start date')
        : schema
    })
})

const SearchPayments = () => {
  const faberToken = OrbitSession.get('faberToken')
  const [payments, setPayments] = React.useState<StripePayment[]>([])

  // Copy to clipboard function
  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${label} copied to clipboard!`)
    } catch (err) {
      toast.error('Failed to copy to clipboard')
    }
  }

  const { mutate: searchPayments, isLoading } = useMutation(
    PaymentAPI.searchPayments,
    {
      onSuccess: (response) => {
        if (response.data?.status === 'SUCCESS') {
          const foundPayments = response.data.data || []
          setPayments(foundPayments)
          toast.success(`${foundPayments.length} payment(s) found.`)
        } else {
          const errorMessage =
            response.data?.error || 'An unknown error occurred while searching for payments.'
          toast.error(errorMessage)
          setPayments([])
        }
      },
      onError: (error: AxiosError<{ error?: string }>) => {
        toast.error(
          error?.response?.data?.error || 'Failed to search for payments.'
        )
        setPayments([])
      }
    }
  )

  return (
    <PageLayout header='Search Payments'>
      <ColorBoxContent>
        <NormalText variant='h4' sx={{ mb: 2 }}>
          Search Stripe Payments
        </NormalText>
        <Formik
          initialValues={{
            last4: '',
            amount: '',
            startDate: null as Date | null,
            endDate: null as Date | null
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            searchPayments({
              faberToken,
              last4: values.last4,
              amount: Number(values.amount) * 100,
              startDate: values.startDate!.toISOString().slice(0, 10),
              endDate: values.endDate!.toISOString().slice(0, 10)
            })
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue
          }) => (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3} alignItems='flex-start'>
                <Grid item xs={12} sm={2.5}>
                  <TextField
                    fullWidth
                    id='last4'
                    name='last4'
                    label='Card Last 4 Digits'
                    value={values.last4}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.last4 && Boolean(errors.last4)}
                    helperText={touched.last4 && errors.last4}
                  />
                </Grid>
                <Grid item xs={12} sm={2.5}>
                  <TextField
                    fullWidth
                    id='amount'
                    name='amount'
                    label='Amount'
                    type='number'
                    value={values.amount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.amount && Boolean(errors.amount)}
                    helperText={touched.amount && errors.amount}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <DatePicker
                          label='Start Date'
                          value={values.startDate}
                          onChange={(newValue) => {
                            setFieldValue('startDate', newValue)
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              error={touched.startDate && Boolean(errors.startDate)}
                              helperText={touched.startDate && errors.startDate}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <DatePicker
                          label='End Date'
                          value={values.endDate}
                          onChange={(newValue) => {
                            setFieldValue('endDate', newValue)
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              error={touched.endDate && Boolean(errors.endDate)}
                              helperText={touched.endDate && errors.endDate}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    color='primary'
                    variant='contained'
                    fullWidth
                    type='submit'
                    disabled={isLoading}
                    sx={{ height: '56px' }}
                  >
                    {isLoading ? <CircularProgress size={24} /> : 'Search'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>

        {payments.length > 0 && (
          <TableContainer component={Paper} sx={{ mt: 4 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Customer Email</TableCell>
                  <TableCell>Customer ID</TableCell>
                  <TableCell>Pack Name</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Payment ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment: StripePayment) => (
                  <TableRow key={payment.stripePaymentId}>
                    <TableCell>
                      <Tooltip title="Click to copy User Email">
                        <span
                          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                          onClick={() => copyToClipboard(payment.userEmail, 'User Email')}
                        >
                          <IconButton size="small">
                            <ContentCopy fontSize="small" />
                          </IconButton>
                          {payment.userEmail}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Click to copy Customer ID">
                        <span
                          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                          onClick={() => copyToClipboard(payment.userId, 'Customer ID')}
                        >
                          <IconButton size="small">
                            <ContentCopy fontSize="small" />
                          </IconButton>
                          {payment.userId}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{payment.name}</TableCell>
                    <TableCell>{`${(payment.amount / 100).toFixed(
                      2
                    )} ${payment.currency.toUpperCase()}`}</TableCell>
                    <TableCell>{payment.status}</TableCell>
                    <TableCell>
                      {new Date(payment.paymentDate).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Click to copy Payment ID">
                        <span
                          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                          onClick={() => copyToClipboard(payment.stripePaymentId, 'Payment ID')}
                        >
                          <IconButton size="small">
                            <ContentCopy fontSize="small" />
                          </IconButton>
                          {payment.stripePaymentId}
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </ColorBoxContent>
    </PageLayout>
  )
}

export default SearchPayments